/**
 * fog-render.ts — v0M5.2 (2026-07-25).
 *
 * Draws the player-faction fog-of-war layer on top of everything except
 * the HUD. Reads `state.visibility.get(playerFactionId)` and paints
 * per-cell alpha per the 4-state model in MAP.md §7.1:
 *
 *   1 — Unexplored          → full opaque void colour (matches background)
 *   2 — Explored, no vision → ~60% opaque wash (terrain shows through, dimmed)
 *   3 — Within vision range → transparent
 *   4 — Vision + Detection  → transparent
 *
 * Implementation is per-frame ImageData at map cell resolution (one
 * pixel per cell). Drawn upscaled via `drawImage` with
 * `imageSmoothingEnabled = false` — blocky edges are appropriate for
 * fog and cost dramatically less than per-pixel writes at zoom
 * resolution.
 *
 * Dev-tools override: `getRenderFlags().revealAllCells === true`
 * short-circuits the paint (no fog drawn) so playtest + debug can see
 * the full map. Wired in dev-console.ts's Vision section.
 *
 * v1 scope: strategic-map only (battle-map fog deferred per v0M5-design
 * scope envelope).
 */

import type { LoadedMap } from './map-loader';
import type { Camera } from './camera';
import { pxPerCell, worldToScreen } from './camera';
import type { GameState, FactionId } from '../../sim/state';
import {
  getCellState,
  CELL_STATE_UNEXPLORED,
  CELL_STATE_EXPLORED,
} from '../../sim/visibility';
import { getRenderFlags } from '../render-flags';

/** Void colour — must match the `#0e0b06` fill in renderer.ts drawFrame. */
const VOID_RGB: readonly [number, number, number] = [14, 11, 6];
/** Alpha for state-2 (explored-but-not-visible) wash. 0.55 = terrain
 *  still legible underneath but obviously dimmed. Tuning candidate. */
const EXPLORED_WASH_ALPHA = 0.55;

let fogBuffer: HTMLCanvasElement | null = null;

/**
 * Draw the fog layer for `playerFactionId`. Called from renderer.ts
 * drawFrame after all entity layers (so fog covers armies etc.) and
 * before the HUD (so map labels stay legible).
 *
 * If the player faction has no visibility map yet (pre-first-tick),
 * this no-ops — the map renders fully visible until the first sim tick
 * seeds the map. Safe fallback; the alternative (paint everything
 * unexplored) would look like the game is broken at boot.
 */
export function drawFog(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  map: LoadedMap,
  camera: Camera,
  canvasW: number,
  canvasH: number,
  playerFactionId: FactionId | null,
): void {
  if (playerFactionId === null) return;
  if (getRenderFlags().revealAllCells) return;
  const vis = state.visibility?.get(playerFactionId);
  if (!vis) return;
  if (vis.width !== map.width || vis.height !== map.height) return;

  // Reuse the per-cell fog buffer across frames to avoid reallocating.
  if (fogBuffer === null || fogBuffer.width !== map.width || fogBuffer.height !== map.height) {
    fogBuffer = document.createElement('canvas');
    fogBuffer.width = map.width;
    fogBuffer.height = map.height;
  }
  const bctx = fogBuffer.getContext('2d');
  if (!bctx) return;

  const img = bctx.createImageData(map.width, map.height);
  const data = img.data;
  const alphaExplored = Math.round(EXPLORED_WASH_ALPHA * 255);
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      const state4 = getCellState(vis, x, y);
      const px = (y * map.width + x) * 4;
      if (state4 === CELL_STATE_UNEXPLORED) {
        data[px] = VOID_RGB[0];
        data[px + 1] = VOID_RGB[1];
        data[px + 2] = VOID_RGB[2];
        data[px + 3] = 255;
      } else if (state4 === CELL_STATE_EXPLORED) {
        data[px] = VOID_RGB[0];
        data[px + 1] = VOID_RGB[1];
        data[px + 2] = VOID_RGB[2];
        data[px + 3] = alphaExplored;
      } else {
        // State 3 or 4 — transparent (visible or detection-visible).
        data[px + 3] = 0;
      }
    }
  }
  bctx.putImageData(img, 0, 0);

  // Blit the cell-resolution fog buffer into the main canvas at the
  // world-to-screen position of (0, 0), upscaled to the current zoom.
  // MFix8b (2026-07-25) — enable bilinear smoothing on the fog blit so
  // per-cell alpha values interpolate softly at the boundary between
  // visible / explored / unexplored regions. Result: the vision ring
  // reads as a rounded circle instead of a blocky per-cell grid. Only
  // the fog layer is smoothed; terrain + entity layers retain their
  // pixel-perfect smoothing settings via save/restore.
  const z = pxPerCell(camera);
  const origin = worldToScreen(camera, canvasW, canvasH, 0, 0);
  const prevSmoothing = ctx.imageSmoothingEnabled;
  const prevQuality = ctx.imageSmoothingQuality;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  const ox = Math.round(origin.x);
  const oy = Math.round(origin.y);
  const mapW = Math.round(map.width * z);
  const mapH = Math.round(map.height * z);
  ctx.drawImage(fogBuffer, ox, oy, mapW, mapH);
  ctx.imageSmoothingEnabled = prevSmoothing;
  ctx.imageSmoothingQuality = prevQuality;

  // MFix8 (2026-07-25) — cover the four border regions outside the map
  // with opaque void colour. City-label text and entity draws can spill
  // past map bounds; without this fill those labels leak through when
  // the map is small enough on screen to leave visible border areas
  // (typical at max zoom out).
  const voidCss = `rgb(${VOID_RGB[0]}, ${VOID_RGB[1]}, ${VOID_RGB[2]})`;
  ctx.fillStyle = voidCss;
  // Left border
  if (ox > 0) ctx.fillRect(0, 0, ox, canvasH);
  // Right border
  const rightEdge = ox + mapW;
  if (rightEdge < canvasW) ctx.fillRect(rightEdge, 0, canvasW - rightEdge, canvasH);
  // Top border (inside the horizontal map strip)
  if (oy > 0) ctx.fillRect(ox, 0, mapW, oy);
  // Bottom border (inside the horizontal map strip)
  const bottomEdge = oy + mapH;
  if (bottomEdge < canvasH) ctx.fillRect(ox, bottomEdge, mapW, canvasH - bottomEdge);
}

/**
 * Helper for entity-draw filters. Returns true iff the given cell is
 * visible to the player faction (state ≥ 3) OR the reveal-all dev flag
 * is on. Consumers: `drawArmies` (skip enemy armies on state-2 or
 * state-1 cells), `drawBattleIcons`, `drawSiegeIcons`.
 *
 * Returns true when: playerFactionId is null (pre-scenario), the
 * visibility map is missing (first-tick lazy path), the reveal flag
 * is on, or the cell state is ≥ 3.
 */
export function isCellVisibleToPlayer(
  state: GameState,
  playerFactionId: FactionId | null,
  x: number,
  y: number,
): boolean {
  if (playerFactionId === null) return true;
  if (getRenderFlags().revealAllCells) return true;
  const vis = state.visibility?.get(playerFactionId);
  if (!vis) return true;
  const cellState = getCellState(vis, Math.floor(x), Math.floor(y));
  return cellState >= 3;
}

/**
 * Stronger variant for Invisibility-tagged entities (Spies at v0M5.3+).
 * Only true when the cell has State 4 (Detection) for the player,
 * or the reveal flag is on. Consumers: draw-spy at v0M5.3.
 */
export function isCellDetectedByPlayer(
  state: GameState,
  playerFactionId: FactionId | null,
  x: number,
  y: number,
): boolean {
  if (playerFactionId === null) return true;
  if (getRenderFlags().revealAllCells) return true;
  const vis = state.visibility?.get(playerFactionId);
  if (!vis) return true;
  return getCellState(vis, Math.floor(x), Math.floor(y)) >= 4;
}
